import { RetailerCheckoutOrderStatusV2 } from "@/api/rest/checkoutApi";
import { ProductCardProps } from "../../types";
import { AppDispatch } from "@/redux/store";
import { MfaValues } from "@/types/misc";
import { NavigateFunction } from "react-router-dom";
import { RetailerConnectionState } from "../../../../types/misc";
import {
  UserInteractionRequirement,
  UserInteractionType,
  UserRetailerCheckoutBatch,
  UserRetailerCheckoutOrder,
} from "../../types";
import { DoneActorEvent, ErrorActorEvent } from "xstate";
import {
  ProductRewardSearchResult,
  UserRetailerShoppingCart,
} from "@brandclub/common-ui";
import { CheckoutIntent } from "../../components/Checkout/types/CheckoutIntent";

type RetailerConnectionInfoList = {
  retailerId: number;
  credential: {
    username?: string;
    password?: string;
    connectionStatus: RetailerConnectionState;
  };
}[];

export enum CheckoutState {
  LOADING = "LOADING",
  PROCESSING = "PROCESSING",
  ACTION_REQUIRED_ERROR = "ACTION_REQUIRED_ERROR",
  NON_ACTION_REQUIRED_ERROR = "NON_ACTION_REQUIRED_ERROR",
  RECEIPT_GENERATED = "RECEIPT_GENERATED",
}

export enum ACTION_REQUIRED_ERROR {
  LOGIN_ERROR = "LOGIN_ERROR",
  OTP_ERROR = "OTP_ERROR", // do polling, only exception for polling
  OTP_TIMEOUT_ERROR = "OTP_TIMEOUT_ERROR",
  SHIPPING_ERROR = "SHIPPING_ERROR",
  DELIVERY_PARTIALLY_OUT_OF_STOCK_ERROR = "DELIVERY_PARTIALLY_OUT_OF_STOCK_ERROR",
  RETAILER_PAYMENT_VERIFICATION = "RETAILER_PAYMENT_VERIFICATION",
  STRIPE_PAYMENT_SELECTION = "STRIPE_PAYMENT_SELECTION",
}

export enum NON_ACTION_REQUIRED_ERROR {
  PAYMENT_ERROR = "PAYMENT_ERROR",
  DELIVERY_ALL_OUT_OF_STOCK_ERROR = "DELIVERY_ALL_OUT_OF_STOCK_ERROR",
}

export enum UserInputUpdateType {
  UPDATE_PAYMENT_CONFIRMATION_AND_COMPLETE_CHECKOUT = "UPDATE_PAYMENT_CONFIRMATION_AND_COMPLETE_CHECKOUT",
  UPDATE_PAYMENT_METHOD = "UPDATE_PAYMENT_METHOD",
  UPDATE_SHIPPING_ADDRESS = "UPDATE_SHIPPING_ADDRESS",
  UPDATE_SHIPPING_OPTION = "UPDATE_SHIPPING_OPTION",
  ADD_SHIPPING_ADDRESS = "ADD_SHIPPING_ADDRESS",
  ADD_PAYMENT_METHOD = "ADD_PAYMENT_METHOD",
  REMOVE_PARTIALLY_OUT_OF_STOCK_ITEM = "REMOVE_PARTIALLY_OUT_OF_STOCK_ITEM",
}

export type SessionOpenState = {
  open?: boolean;
  chevronShow?: boolean;
  errorMessage?: string;
};

export type CheckoutMachineContext = {
  orderId?: string;
  sectionState: {
    account: SessionOpenState;
    payment: SessionOpenState;
    shipping: SessionOpenState;
    delivery: SessionOpenState;
  };
  products: Record<string, ProductRewardSearchResult>;
  checkoutState?: CheckoutState;
  retailerIds?: number[];
  retailerConnectionInfoList?: RetailerConnectionInfoList;
  activeCheckoutRetailerId?: number;
  checkoutBatch?: UserRetailerCheckoutBatch;
  activeCheckoutOrder?: UserRetailerCheckoutOrder;
  retailerCheckoutOrderStatus?: RetailerCheckoutOrderStatusV2;
  checkoutAllCarts?: Partial<UserRetailerShoppingCart>[];
  error?: {
    stage?: number;
    type: ACTION_REQUIRED_ERROR | NON_ACTION_REQUIRED_ERROR;
    interactionRequired?: UserInteractionRequirement;
    errorMessage?: string;
  };
  activeStage?: number;
  userInputError?: Error;
  enterAnimationConfig?: {
    title: string;
    checkoutCards: ProductCardProps[];
  };
  otpStatus?: MfaValues;
  checkoutRetailerConnectionState?: {
    retailerId: number;
    type: "INITIAL_CONNECT" | "RETRY_LOGIN";
    errorMessage?: string;
  };
  navigate: NavigateFunction;
  dispatch: AppDispatch;
  userInputUpdateType?: UserInputUpdateType;
  animationCompleted?: boolean;
  /**
   * Indicates if the user had to enter login info for a retailer.
   */
  hasUserLoggedInRetailer?: boolean;
};

export type CheckoutSectionOpenEvent = {
  type: "ON_SESSION_OPEN";
  data: {
    sessionToOpen: "account" | "payment" | "shipping" | "delivery";
  };
};

export type CheckoutSectionCloseEvent = {
  type: "ON_SESSION_CLOSE";
  data: {
    sessionToClose: "account" | "payment" | "shipping" | "delivery";
  };
};

export type CheckoutRetryCheckoutEvent = {
  type: "ON_RETRY_CHECKOUT";
  data?: {
    payment?: {
      paymentMethodId?: string;
      billingAddress?: CheckoutIntent["billingAddress"];
      shippingAddress?: CheckoutIntent["shippingAddress"];
    };
    retailerId?: number;
    credential?: {
      username: string;
      password: string;
    };
  };
};

export type CheckoutOnRetailerPaymentVerificationEvent = {
  type: "ON_RETAILER_PAYMENT_VERIFICATION";
};

export type CheckoutOnRetailerCredentialsEvent = {
  type: "ON_RETAILER_CREDENTIALS";
};

export type CheckoutOnOtpVerificationEvent = {
  type: "ON_OTP_VERIFICATION";
};

export type CheckoutOnAddressSelectionEvent = {
  type: "ON_ADDRESS_SELECTION";
};

export type CheckoutOnInventoryConfirmationEvent = {
  type: "ON_INVENTORY_CONFIRMATION";
};

export type CheckoutOnShippingOptionSelectionEvent = {
  type: "ON_SHIPPING_OPTION_SELECTION";
};

export type CheckoutSwitchToGuestEvent = {
  type: "ON_SWITCH_TO_GUEST";
};

export type CheckoutSubmitLoginInfoEvent = {
  type: "SUBMIT_LOGIN_INFO";
  data: {
    username?: string;
    password?: string;
  };
};

export type CheckoutSubmitOtpEvent = {
  type: "SUBMIT_OTP";
  data: {
    otp?: string;
  };
};

export type CheckoutResendOtpEvent = {
  type: "RESEND_OTP";
};

export type CheckoutOtpTimeoutRetryEvent = {
  type: "OTP_TIME_OUT_RETRY";
};

export type CheckoutUpdateShippingAddressEvent = {
  type: "UPDATE_SHIPPING_ADDRESS";
};

export type CheckoutConfirmPartiallyOutOfStockEvent = {
  type: "CONFIRM_PARTIALLY_OUT_OF_STOCK";
};

export type CheckoutSubmitUserInteractionEvent = {
  type: "SUBMIT_USER_INTERACTION";
  data: {
    interactionType: UserInteractionType;
    interactionData: UserRetailerCheckoutCommunicationResponse;
  };
};

export type CheckoutUpdateRetailerCheckoutOrderEvent = {
  type: "UPDATE_RETAILER_CHECKOUT_ORDER";
  data: {
    retailerCheckoutOrder: UserRetailerCheckoutOrder;
  };
};

export type CheckoutMachineEvents =
  | CheckoutSectionOpenEvent
  | CheckoutSectionCloseEvent
  | CheckoutSubmitLoginInfoEvent
  | CheckoutSubmitOtpEvent
  | CheckoutResendOtpEvent
  | CheckoutOtpTimeoutRetryEvent
  | CheckoutUpdateShippingAddressEvent
  | CheckoutConfirmPartiallyOutOfStockEvent
  | DoneActorEvent
  | ErrorActorEvent
  | CheckoutSubmitUserInteractionEvent
  | CheckoutRetryCheckoutEvent
  | CheckoutSwitchToGuestEvent
  | CheckoutUpdateRetailerCheckoutOrderEvent
  | CheckoutOnRetailerPaymentVerificationEvent
  | CheckoutOnRetailerCredentialsEvent
  | CheckoutOnOtpVerificationEvent
  | CheckoutOnAddressSelectionEvent
  | CheckoutOnInventoryConfirmationEvent
  | CheckoutOnShippingOptionSelectionEvent;

export type UserRetailerCheckoutRetailerCredentialsCommunicationResponse = {
  credential: {
    username: string;
    password: string;
  };
  retailerId: number;
};

export type UserRetailerCheckoutOtpCommunicationResponse = {
  otpCode?: string;
  resend?: boolean;
};

export type UserRetailerCheckoutShippingAddressSelectionCommunicationResponse =
  {
    address1: string;
    address2: string;
    city: string;
    state: string;
    zipCode: string;
    firstName: string;
    lastName: string;
  };

export type UserRetailerCheckoutInventoryConfirmationCommunicationResponse = {
  approve: boolean;
};

export type UserRetailerCheckoutShippingOptionSelectionCommunicationResponse = {
  shippingOption: {
    shippingGroups: {
      products: {
        retailerSku: string;
        stacklineSku?: string;
        quantity: number;
        retailPrice: number;
      }[];
      deliveryOptions: {
        name: string;
        cssSelector: string;
        deliveryTimeWindows?: {
          name: string;
          cssSelector: string;
          isSelected: boolean;
        }[];
        isSelected: boolean;
      }[];
    }[];
  };
};

export type UserRetailerCheckoutPaymentVerificationCommunicationResponse = {
  value: string;
};

export type UserRetailerCheckoutCommunicationResponse =
  | UserRetailerCheckoutRetailerCredentialsCommunicationResponse
  | UserRetailerCheckoutOtpCommunicationResponse
  | UserRetailerCheckoutShippingAddressSelectionCommunicationResponse
  | UserRetailerCheckoutInventoryConfirmationCommunicationResponse
  | UserRetailerCheckoutShippingOptionSelectionCommunicationResponse
  | UserRetailerCheckoutPaymentVerificationCommunicationResponse;
